import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { API } from '../../../api/apis.config'
import {
  PriorityEnum,
  priorityEnumMapping
} from '../../../lib/types/enums/staff-application/priority.enum'
import { PcrLight } from '../../../lib/types/models/pcr/pcr'
import { AssignPcrInput } from '../../../lib/types/models/staff-application/staff-application-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDynamicSelectInput from '../../elements/inputs/controlled-dynamic-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: ZodType<AssignPcrInput> = z.object({
  pcr: z.object(
    {
      id: createNumberSchema('id'),
      firstName: createStringSchema('prénom'),
      lastName: createStringSchema('NOM')
    },
    { required_error: 'Veuillez choisir un PCR', invalid_type_error: 'Veuillez choisir un PCR' }
  ),
  priority: createEnumSchema({ fieldName: 'priorité', enumType: PriorityEnum })
})

type AssignPcrFormProps = BaseFormProps<AssignPcrInput, true> & {
  className?: string
}

const AssignPcrForm = ({
  defaultValues,
  className,
  ...validationButtonProps
}: AssignPcrFormProps) => {
  const form = useForm<AssignPcrInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <FormField
            control={form.control}
            name="pcr"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDynamicSelectInput<PcrLight>
                    {...field}
                    required
                    dropDownPosition="above"
                    endpoint={API.pcr.getAll.url}
                    mapper={(record) => ({
                      label: record.firstName + ' ' + record.lastName,
                      value: record.id
                    })}
                    label="Sélectionner le PCR"
                    selectedOption={field.value}
                    onChange={(option) => {
                      if (option.value)
                        field.onChange({
                          id: option.value,
                          firstName: option.label.split(' ')[0],
                          lastName: option.label.split(' ')[1]
                        })
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="priority"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledSelectInput<PriorityEnum>
                    {...field}
                    required
                    options={Object.values(PriorityEnum).map((priority) => ({
                      label: priorityEnumMapping[priority].label,
                      value: priority
                    }))}
                    dropDownPosition="above"
                    label={'Priorité'}
                    selectedOption={getSelectedEnumOption(field.value, priorityEnumMapping)}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default AssignPcrForm
